// Numbers for the pricing from 1 November 2026 (/pricing-2026) and the "what's changing" page
// (/pricing-changes-2026). Words live in the per-locale copy files next to this one
// (copy.en.ts, copy.es.ts, copy.pt.ts); every price, rate and allowance lives here, so both pages
// and the tests read the same figures.
//
// Plans are billed in US dollars everywhere.

import { BIOMETRIC_POLICY_PATH as LEGAL_BIOMETRIC_POLICY_PATH } from "@/content/legal/biometric.en";

/**
 * True until launch: /pricing-2026 stays noindex,nofollow (and unlinked from the nav, footer and
 * sitemap). See the launch-day notes in src/pages/Pricing2026.tsx.
 */
export const PRELAUNCH = false;

/**
 * The "what's changing" page /pricing-changes-2026 is public (indexable, linkable) from the
 * announcement on 20 October 2026, ahead of the 1 November launch: the announcement email links to
 * it. Independent of PRELAUNCH.
 */
export const CHANGES_PUBLIC = true;

/**
 * The announcement day (the email to every workspace). Nothing customers browse to links the changes page
 * before it: the footer's "Pricing changes 2026" link appears from 00:00 UTC that day.
 */
export const CHANGES_ANNOUNCED_ISO = "2026-10-20";

/** Has the 1 November change been announced at `now` (so the site may link the changes page)? */
export const changesAnnounced = (now: number = Date.now()): boolean =>
  CHANGES_PUBLIC && now >= Date.parse(`${CHANGES_ANNOUNCED_ISO}T00:00:00Z`);

/** Where the two pages live until launch. */
export const PRICING_PATHS = { pricing: "/pricing", changes: "/pricing-changes-2026" };

/**
 * The date everyone moves to the new pricing. Commission, storage and allowances change on this
 * date; Max's $49 applies from a workspace's first renewal on or after it; Lite (and Pay as you go)
 * workspaces move to the new $0 Lite on it.
 */
export const CHANGE_DATE_ISO = "2026-11-01";

/** Always-on shared FTP servers close on this date (owner decision 17 Oct 2026: the same day as the new
 * pricing; it was 1 December). On-demand FTP servers are available now on every plan (19 Oct 2026). */
export const SHARED_FTP_RETIRES_ISO = "2026-11-01";

/** Is the always-on shared FTP still open at `now`? It closes at 00:00 UTC on SHARED_FTP_RETIRES_ISO. */
export const sharedFtpOpen = (now: number = Date.now()): boolean => now < Date.parse(`${SHARED_FTP_RETIRES_ISO}T00:00:00Z`);

/**
 * The biometric data retention policy page, linked from the "privacy" point on the changes page.
 * The route is live (src/pages/BiometricRetentionPolicy.tsx); the path is owned by the legal content
 * so the two can't drift. Set to null to hide the link.
 */
export const BIOMETRIC_POLICY_PATH: string | null = LEGAL_BIOMETRIC_POLICY_PATH;

/** Team size per plan: people in a workspace. null = unlimited. Applies to new invites only. */
export const TEAM_LIMITS: Record<"free" | "pro" | "max", number | null> = { free: 3, pro: 10, max: null };

export type NewPlanId = "free" | "pro" | "max";
export type TodayPlanId = "payg" | "lite" | "pro" | "enterprise";

export interface NewPlan {
  /**
   * Stable internal id, shared with serverless lib/plans.js, the admin web, the gallery app and Studio. The
   * $0 plan's id is "free" but customers see it as "Lite" (owner decision, 19 Oct 2026: Lite replaces the
   * free plan on the same terms). Show `name`, never the id.
   */
  id: NewPlanId;
  /** Plan names are product names and aren't translated. */
  name: string;
  /** US$ a month. */
  priceUsd: number;
  /** Photohawk's commission on sales (0–1). The only Photohawk fee on a sale; no fee per order. */
  commissionRate: number;
  /** Storage included, in GB (1 TB = 1,000 GB). */
  storageGb: number;
  /** Photos in free-download galleries included each month. */
  freeDownloads: number;
  featured?: boolean;
}

export const NEW_PLANS: NewPlan[] = [
  { id: "free", name: "Lite", priceUsd: 0, commissionRate: 0.16, storageGb: 150, freeDownloads: 1000 },
  { id: "pro", name: "Pro", priceUsd: 29, commissionRate: 0.14, storageGb: 1000, freeDownloads: 5000, featured: true },
  { id: "max", name: "Max", priceUsd: 49, commissionRate: 0.12, storageGb: 3000, freeDownloads: 10000 },
];

/** Free-download photos above a plan's monthly allowance: the same on every plan. */
export const FREE_DOWNLOAD_OVERAGE = { perThousandUsd: 8, perPhotoUsd: 0.008 };

/** Extra storage packs, US$ a month. Billed automatically as the cheapest mix that covers what you store. */
export interface StoragePack {
  id: string;
  gb: number;
  priceUsd: number;
}
export const STORAGE_PACKS: StoragePack[] = [
  { id: "100gb", gb: 100, priceUsd: 5 },
  { id: "1tb", gb: 1000, priceUsd: 25 },
  { id: "5tb", gb: 5000, priceUsd: 100 },
];

/**
 * Today's plans (until 31 October 2026), for comparisons only. Mirrors
 * src/components/workspace/PlanSelector.tsx and the live /pricing page (serverless plans.js).
 * "Enterprise" is today's top plan; it's renamed Max (same plan, new name), so the pages show it as
 * a name change rather than a move. Pay as you go and Lite both move to the new Lite ($0 a month, 16%).
 */
export interface TodayPlan {
  id: TodayPlanId;
  name: string;
  priceUsd: number;
  commissionRate: number;
  storageGb: number;
  /** US$ per photo uploaded to a free-download gallery (every photo is charged today). */
  freePhotoUsd: number;
  /** The plan this one moves to on 1 November 2026. */
  movesTo: NewPlanId;
  /** True when it's the same plan under a new name (Enterprise → Max), not a move to another plan. */
  renamed?: boolean;
}

export const TODAY_PLANS: TodayPlan[] = [
  { id: "payg", name: "Pay as you go", priceUsd: 0, commissionRate: 0.25, storageGb: 25, freePhotoUsd: 0.02, movesTo: "free" },
  { id: "lite", name: "Lite", priceUsd: 16.99, commissionRate: 0.185, storageGb: 100, freePhotoUsd: 0.0135, movesTo: "free" },
  { id: "pro", name: "Pro", priceUsd: 29.99, commissionRate: 0.16, storageGb: 500, freePhotoUsd: 0.01, movesTo: "pro" },
  { id: "enterprise", name: "Enterprise", priceUsd: 44.99, commissionRate: 0.135, storageGb: 1000, freePhotoUsd: 0.008, movesTo: "max", renamed: true },
];

/** Today's extra storage, US$ per GB a month above the plan's allowance. */
export const TODAY_EXTRA_STORAGE_PER_GB_USD = 0.023;

export const newPlan = (id: NewPlanId): NewPlan => {
  const p = NEW_PLANS.find((x) => x.id === id);
  if (!p) throw new Error(`unknown plan ${id}`);
  return p;
};

/** The lowest commission on any plan (Max). */
export const LOWEST_RATE = Math.min(...NEW_PLANS.map((p) => p.commissionRate));

/**
 * The worked example on /pricing-2026: a UK order on the photographer's own Stripe account.
 * Stripe charges its card fee to that account, not through Photohawk; the rate is Stripe's
 * standard UK card rate as listed in src/components/TransactionFeesInfo.tsx.
 */
export const WORKED_EXAMPLE = {
  currency: { code: "GBP", prefix: "£", digits: 2 },
  order: 15,
  cardFee: { percent: 0.015, fixed: 0.2 },
};

/**
 * "Compare every feature" on /pricing-2026: which plan has what, built from the real gating, not marketing.
 * Sources (keep in step): photohawk-serverless lib/planEntitlements.js PLAN_FEATURES (customDomain, analytics →
 * Pro and Max; video, managedSupport, revenueSplit → Max; previewFirst, onDemandFtp → every plan since 19 Oct 2026), lib/plans.js
 * (rates, storage, free downloads, team limits), lib/customDomains.js MAX_CUSTOM_DOMAINS (3), lib/teamSeats.js
 * (gallery collaborators hold a seat) and lib/uploadGate.js (video is Max). Everything else listed here has no plan
 * check anywhere (web, serverless or the gallery app), so it's on every plan.
 *
 * Left out on purpose until the owner decides (no code gates them, but the plan cards above describe them per
 * plan): logo / colours / fonts, removing Photohawk branding, multiple brands, and support for the workspace itself
 * beyond the "support" row's card wording.
 *
 * A cell is `true` (✓), `false` (not included) or a `value` key in the copy's `pricing.compare.values`, filled with
 * `vars` (a number is formatted for the language; `storageGb` as GB / TB; `rate` as a percentage).
 */
export type CompareValueKey =
  | "rate"
  | "storage"
  | "perMonth"
  | "upToPeople"
  | "unlimited"
  | "upToDomains"
  | "months"
  | "fromNovember"
  | "packs"
  | "overage"
  | "supportHelp"
  | "supportEmail"
  | "supportPriority";
export type CompareCell = boolean | { value: CompareValueKey; vars?: Record<string, number> };
export interface CompareRow {
  id: string;
  cells: Record<NewPlanId, CompareCell>;
}
export interface CompareSection {
  id: "selling" | "finding" | "galleries" | "brand" | "insights" | "team" | "uploading" | "storage" | "support";
  rows: CompareRow[];
}

const every = (): Record<NewPlanId, CompareCell> => ({ free: true, pro: true, max: true });
const proUp = (): Record<NewPlanId, CompareCell> => ({ free: false, pro: true, max: true });
const maxOnly = (): Record<NewPlanId, CompareCell> => ({ free: false, pro: false, max: true });
const perPlan = (f: (p: NewPlan) => CompareCell): Record<NewPlanId, CompareCell> =>
  ({ free: f(newPlanRow("free")), pro: f(newPlanRow("pro")), max: f(newPlanRow("max")) });
function newPlanRow(id: NewPlanId): NewPlan {
  return NEW_PLANS.find((p) => p.id === id)!;
}

/** Custom domains per workspace on the plans that have them (serverless customDomains.js MAX_CUSTOM_DOMAINS). */
export const MAX_CUSTOM_DOMAINS = 3;
/** Face data for selfie search is deleted this many months after a photo is uploaded (from 1 November 2026). */
export const FACE_DATA_MONTHS = 12;

export const COMPARE_SECTIONS: CompareSection[] = [
  {
    id: "selling",
    rows: [
      { id: "commission", cells: perPlan((p) => ({ value: "rate", vars: { rate: p.commissionRate } })) },
      { id: "products", cells: every() },
      { id: "packages", cells: every() },
      { id: "coupons", cells: every() },
      { id: "preorder", cells: every() },
      { id: "whatsapp", cells: every() },
      { id: "payouts", cells: every() },
      // Revenue splitting: Max only (lib/planEntitlements PLAN_FEATURES.revenueSplit).
      { id: "revenueSplit", cells: maxOnly() },
    ],
  },
  {
    id: "finding",
    rows: [
      { id: "selfie", cells: every() },
      { id: "bib", cells: every() },
      { id: "time", cells: every() },
      { id: "find", cells: every() },
      { id: "profiles", cells: every() },
      { id: "faceData", cells: perPlan(() => ({ value: "months", vars: { n: FACE_DATA_MONTHS } })) },
    ],
  },
  {
    id: "galleries",
    rows: [
      { id: "galleries", cells: every() },
      { id: "private", cells: every() },
      { id: "protection", cells: every() },
      { id: "watermarks", cells: every() },
      { id: "freeDownloads", cells: perPlan((p) => ({ value: "perMonth", vars: { n: p.freeDownloads } })) },
      { id: "freeOverage", cells: perPlan(() => ({ value: "overage", vars: { usd: FREE_DOWNLOAD_OVERAGE.perThousandUsd } })) },
      { id: "video", cells: maxOnly() },
    ],
  },
  {
    id: "brand",
    rows: [
      { id: "address", cells: every() },
      { id: "customDomain", cells: { free: false, pro: { value: "upToDomains", vars: { n: MAX_CUSTOM_DOMAINS } }, max: { value: "upToDomains", vars: { n: MAX_CUSTOM_DOMAINS } } } },
      { id: "pages", cells: every() },
      { id: "emails", cells: every() },
      { id: "logo", cells: every() },
      { id: "noBadge", cells: proUp() },
    ],
  },
  {
    id: "insights",
    rows: [
      { id: "live", cells: every() },
      { id: "analytics", cells: proUp() },
      { id: "sales", cells: every() },
      { id: "collectionDownloads", cells: every() },
    ],
  },
  {
    id: "team",
    rows: [
      {
        id: "people",
        cells: perPlan((p) => {
          const n = TEAM_LIMITS[p.id];
          return n === null ? { value: "unlimited" } : { value: "upToPeople", vars: { n } };
        }),
      },
      { id: "roles", cells: every() },
      { id: "collaborators", cells: every() },
    ],
  },
  {
    id: "uploading",
    rows: [
      { id: "webUploader", cells: every() },
      { id: "previewsFirst", cells: every() },
      { id: "soldFirst", cells: every() },
      { id: "originalsBy", cells: every() },
      { id: "ftp", cells: every() },
      { id: "cloudImport", cells: every() },
      { id: "studioApp", cells: perPlan(() => ({ value: "fromNovember" })) },
    ],
  },
  {
    id: "storage",
    rows: [
      { id: "included", cells: perPlan((p) => ({ value: "storage", vars: { storageGb: p.storageGb } })) },
      { id: "packs", cells: perPlan(() => ({ value: "packs", vars: { usd: STORAGE_PACKS[0].priceUsd, gb: STORAGE_PACKS[0].gb } })) },
    ],
  },
  {
    id: "support",
    rows: [
      { id: "you", cells: { free: { value: "supportHelp" }, pro: { value: "supportEmail" }, max: { value: "supportPriority" } } },
      { id: "buyers", cells: maxOnly() },
    ],
  },
];
